// app/providers.tsx
"use client"

import { usePathname, useSearchParams } from "next/navigation"
import { useEffect, Suspense } from "react"
import { usePostHog } from "posthog-js/react"
import posthog from "posthog-js"
import { PostHogProvider as PHProvider } from "posthog-js/react"
import { useStytchUser } from "@stytch/nextjs"
import { useOrganization } from "@/lib/contexts/OrganizationContext"

export function PostHogProvider({ children }: { children: React.ReactNode }) {
    useEffect(() => {
        // Check if localhost
        const isLocalhost =
            typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")

        posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
            api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com",
            person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
            capture_pageview: false, // Disable automatic pageview capture, as we capture manually
            disable_session_recording: isLocalhost // Disable session recording on localhost
        })
    }, [])

    return (
        <PHProvider client={posthog}>
            <SuspendedPostHogPageView />
            <PostHogUserIdentifier />
            {children}
        </PHProvider>
    )
}

function PostHogPageView() {
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const posthog = usePostHog()

    // Track pageviews
    useEffect(() => {
        if (pathname && posthog) {
            let url = window.origin + pathname
            if (searchParams.toString()) {
                url = url + "?" + searchParams.toString()
            }

            posthog.capture("$pageview", { $current_url: url })
        }
    }, [pathname, searchParams, posthog])

    return null
}

function PostHogUserIdentifier() {
    const posthog = usePostHog()
    const { user, isInitialized: stytchInitialized } = useStytchUser()
    const { userEmail, isAdmin, organization, loading: orgLoading } = useOrganization()

    useEffect(() => {
        // Wait for both Stytch and Organization to load
        if (!stytchInitialized || orgLoading || !posthog) {
            return
        }

        // If no user, reset PostHog identity
        if (!user || !userEmail) {
            posthog.reset()
            return
        }

        // Don't identify admin users (@trycardinal.ai), except devi@trycardinal.ai
        if (isAdmin && userEmail !== "devi@trycardinal.ai") {
            console.log("🚫 PostHog: Skipping @trycardinal.ai user (except devi@trycardinal.ai)")
            posthog.set_config({ disable_session_recording: true })
            return
        }


        // Identify real customer users
        console.log("✅ PostHog: Identifying user:", userEmail)
        posthog.identify(userEmail, {
            email: userEmail,
            domain: organization?.domain || userEmail.split("@")[1],
            account_name: organization?.account_name || organization?.name
        })

        // Enable session recording for customers (if not localhost)
        const isLocalhost =
            typeof window !== "undefined" && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")

        if (!isLocalhost) {
            posthog.set_config({ disable_session_recording: false })
        }
    }, [user, userEmail, isAdmin, organization, stytchInitialized, orgLoading, posthog])

    return null
}

// Wrap PostHogPageView in Suspense to avoid the useSearchParams usage above
// from de-opting the whole app into client-side rendering
// See: https://nextjs.org/docs/messages/deopted-into-client-rendering
function SuspendedPostHogPageView() {
    return (
        <Suspense fallback={null}>
            <PostHogPageView />
        </Suspense>
    )
}
